/**
 * feature/dashboard — spec §F7 (Monitoring & decision-support dashboard).
 *
 * One `/dashboard` fetch (Task 6's `fetchDashboard`) answers the whole page: KPI rail,
 * targets with the server's own insight sentences, the B.2.1/ISFM trend, the GESI shares
 * and the per-domain freshness register. The cards are `DashboardCards.tsx`; this file is
 * the fetch, the three filters, the source line, and the PDF report job.
 *
 * **Geography is the global country.** The mockup's own comment on this handler reads
 * *"one path for geography changes: map layers, boundaries and dashboard all stay in
 * sync"* — so the Geography select writes `setCountry`, the same store field the topbar's
 * select and the map read. Domain and Year do **not** work that way: they are `dashDomain`
 * and `dashYear`, session-local to this page (see their own comments in `lib/state.ts`),
 * because browsing the dashboard's year filter must not move the map underneath the
 * reader. `resolveDashYear` is the rule: the local override when one is set, the map's
 * `selectYear` otherwise.
 *
 * The report button is §E8's `POST /analysis/report` on the §D6 submit envelope — the same
 * submit-then-poll shape `map/ExportModal.tsx` uses, including its `timer`/`alive` refs so
 * a poll cannot outlive the page, and its cache-hit short circuit (a `cached` submission
 * that is already `done` links immediately and never polls).
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Trans, useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import {
  dashboardCsvUrl,
  fetchAnalysisJob,
  fetchConfig,
  fetchDashboard,
  fetchIndicators,
  fetchLayers,
  fetchUnitOptions,
  queryKeys,
  submitReport,
  type UnitOption,
  type UnitType,
} from '../../lib/api';
import { Icon } from '../../lib/icons';
import {
  COUNTRY_NAMES,
  scopeFromUnit,
  selectYear,
  useAppStore,
  YEARS,
  type Country,
} from '../../lib/state';
import { toast } from '../../lib/toast';
import { nextDelay, POLL_START_MS, POLL_TIMEOUT_MS } from '../map/exportOptions';
import { selectTheme } from '../sidebar/applyTheme';
import { BASELINE_PREFIX, INDICATORS_THEME, programmeInfoAllowed } from '../sidebar/themes';
import { BaselineSection } from './BaselineSection';
import {
  csvFilename,
  outcomeCards,
  resolveDashYear,
  sectionSpec,
  splitByTier,
  withTargets,
  type DashboardSectionKey,
} from './dashboard';
import {
  HeroKpiTile,
  InsightsCard,
  OutcomeCards,
  TargetsCard,
  TrendCard,
} from './DashboardCards';
import { DashSection } from './SectionHeader';
import { SectionNav } from './SectionNav';

/** §G8's fixed country order — the same list the topbar's own select offers. */
const COUNTRIES: Country[] = ['ALL', 'BFA', 'MLI', 'NER', 'NGA'];

/**
 * §1 — the numeral each section is printed with, keyed off the one ordered spec
 * (`dashboard.ts`'s `sectionSpec`) so the discs, the nav pills and the source of truth for
 * the order cannot drift apart. The numbering is **fixed**, not a running count: a payload
 * with no outcome rows still leaves Baseline at III, because the client numbered the
 * sections, not whichever of them happen to have rows today.
 */
const NUMERAL = Object.fromEntries(
  sectionSpec.map((section) => [section.key, section.numeral]),
) as Record<DashboardSectionKey, string>;

type ReportPhase = 'idle' | 'running' | 'done' | 'error';

/**
 * One `/hierarchy/options` list for a scope select (client item 7).
 *
 * **Operational only**, exactly as the map's own `CascadeFilter` asks for it (R6): a unit is
 * listed when it, or something beneath it, carries a located MELS activity or an approved
 * MELS-sourced value. That is the right shortlist for a KPI dashboard for the same reason it
 * is the right one for the map — the 981 communes the programme does not report from would
 * answer with a rail of zeroes, which is a page the reader cannot use — and it keeps the two
 * controls offering one list rather than two (controller ruling, fix round 1). The server
 * never answers `[]`: where the shortlist would be empty it returns the unfiltered rows.
 *
 * Sharing the flag also shares the cache with the map's cascade, so walking one control and
 * then the other is one fetch per rung, not two.
 *
 * `enabled` is the caller's gate; the hierarchy changes when IFDC re-ingests, not while a
 * reader is clicking, so the lists are held for half an hour like the map's own.
 */
function useScopeOptions(
  type: UnitType,
  level: number | null,
  country: Country,
  parent: string | null,
  enabled: boolean,
) {
  return useQuery({
    queryKey: queryKeys.unitOptions(type, level, country, parent),
    queryFn: () => fetchUnitOptions(type, level, country, parent),
    enabled,
    staleTime: 30 * 60 * 1000,
  }).data;
}

interface ScopeSelectProps {
  id: string;
  testId: string;
  label: string;
  /** The "no pick at this rung" entry — `All regions`, `All watersheds`, … */
  anyLabel: string;
  value: string;
  options: UnitOption[] | undefined;
  disabled?: boolean;
  onPick: (option: UnitOption | null) => void;
}

/**
 * One rung of the dashboard's scope filter, as a labelled field in the `.dash-filters`
 * grid — the label sits *above* its select, and every field is one cell of a fixed grid, so
 * the seven controls line up in columns instead of wrapping into ragged rows (client,
 * 2026-09-16: "align everything, arrange everything neatly").
 */
function ScopeSelect({
  id, testId, label, anyLabel, value, options, disabled, onPick,
}: ScopeSelectProps) {
  return (
    <div className="dash-field">
      <label htmlFor={id}>{label}</label>
      <select
        id={id}
        data-testid={testId}
        value={value}
        disabled={disabled || !options}
        onChange={(e) =>
          onPick(e.target.value ? (options ?? []).find((o) => o.id === e.target.value) ?? null : null)
        }
      >
        <option value="">{anyLabel}</option>
        {(options ?? []).map((o) => (
          <option key={o.id} value={o.id}>
            {o.name}
          </option>
        ))}
        {/*
          A pick whose list has not landed yet would otherwise fall back to the first entry
          and *look* like "All regions" over a filtered page — the same guard the map's
          `CascadeFilter` carries.
        */}
        {value && !(options ?? []).some((o) => o.id === value) ? (
          <option value={value}>{value}</option>
        ) : null}
      </select>
    </div>
  );
}

/** `2026-08-06T04:00:00Z` → `2026-08-06 04:00 UTC`, the mockup's own register. */
function syncStamp(iso: string): string {
  return `${iso.slice(0, 10)} ${iso.slice(11, 16)} UTC`;
}

export function DashboardPage() {
  const { t } = useTranslation();

  const country = useAppStore((s) => s.country);
  const setCountry = useAppStore((s) => s.setCountry);
  const dashDomain = useAppStore((s) => s.dashDomain);
  const setDashDomain = useAppStore((s) => s.setDashDomain);
  const dashYear = useAppStore((s) => s.dashYear);
  const setDashYear = useAppStore((s) => s.setDashYear);
  const role = useAppStore((s) => s.role);
  const navigate = useNavigate();
  const storeYear = useAppStore(selectYear);
  const year = resolveDashYear(storeYear, dashYear);

  const { data: config } = useQuery({ queryKey: queryKeys.config(), queryFn: fetchConfig });
  /*
    The other two catalogues `applyThemeDefault` reads, for the Baseline section's "View on
    map" (§1). Fetched here for the same reason `catalogue/preview.ts` fetches them: a theme
    selection applies that theme's default dataset, and a half-loaded table applies nothing
    rather than a wrong thing. Both are already cached in any session that has been to the
    explorer, so this costs a dashboard-first visit one request each.
  */
  const { data: layers } = useQuery({ queryKey: queryKeys.layers(role), queryFn: fetchLayers });
  const { data: indicators } = useQuery({
    queryKey: queryKeys.indicators(),
    queryFn: fetchIndicators,
  });

  /* ---- the §F7 scope filter: region → commune, watershed, partnership (client item 7) ---- */

  /**
   * The unit the page is reporting for, **page-local** — deliberately not `state.scope`.
   *
   * The map's cascade writes that field, and a dashboard that wrote it too would move the
   * explorer underneath a reader who only came here to look at one region's numbers: the same
   * reason Year and Domain are `dashYear`/`dashDomain` rather than the map's own. The store
   * hears about the pick at exactly one moment — "View on map" — and the whole `UnitOption`
   * is held rather than the three fields the fetch needs, so that gesture can hand
   * `scopeFromUnit` the row `/hierarchy/options` answered with instead of a reconstruction.
   */
  const [unit, setUnit] = useState<UnitOption | null>(null);
  const unitId = unit?.id ?? null;

  /**
   * The four selects are *derived* from that one unit, never stored per-rung — the map's
   * cascade rule (`CascadeFilter`), and what makes the two branches exclusive without a line
   * of code enforcing it: a scope is one unit, so a watershed pick simply leaves the Region
   * box with nothing to show, and a commune keeps its region standing above it via `parent_id`.
   */
  const isAdmin = unit?.unit_type === 'admin';
  const pickedCommune = isAdmin && unit?.admin_level === 2 ? unit.id : '';
  const pickedRegion = isAdmin ? (pickedCommune ? unit?.parent_id ?? '' : unit?.id ?? '') : '';
  const pickedWatershed = unit?.unit_type === 'watershed' ? unit.id : '';
  const pickedPartnership = unit?.unit_type === 'partnership' ? unit.id : '';

  const regions = useScopeOptions('admin', 1, country, null, true);
  const communes = useScopeOptions('admin', 2, 'ALL', pickedRegion || null, Boolean(pickedRegion));
  const watersheds = useScopeOptions('watershed', null, country, null, true);
  const partnerships = useScopeOptions('partnership', null, country, null, true);

  /**
   * A country change clears the scope. A Burkinabè region left selected over Malian figures
   * would be a filter the page cannot honour and a label that lies about the numbers — and
   * the lists themselves have just been replaced underneath it.
   *
   * The Geography select clears it in the same handler as well, so the country and the unit
   * move in one render rather than the page firing a `?unit=` read for a unit it is about to
   * drop. This effect is the backstop for any *other* write of the global country.
   */
  useEffect(() => {
    setUnit(null);
  }, [country]);

  /** Emptying the Commune box means "back to the region", not "clear the filter". */
  const regionOption = (regions ?? []).find((o) => o.id === pickedRegion) ?? null;

  /**
   * "View on map" on a KPI tile or a target row (client, 2026-09-15): the explorer with this
   * indicator mapped. It is the rail's own Indicators-icon gesture done by hand — the pane
   * switches to Indicators, the theme filter follows (`DomainRail.select` does the same two
   * things), the indicator is set, and only then the route changes, so the map's first paint
   * already has the choropleth. Offered only when the Indicators icon itself would be
   * (`programmeInfoAllowed`): a guest under a closed portal gets no button rather than a
   * button to an empty pane.
   */
  const canMap = programmeInfoAllowed(config, role);
  const viewOnMap = useCallback(
    (indicatorId: string) => {
      const store = useAppStore.getState();
      store.switchSidebarTab('inds');
      store.setDomFilter(INDICATORS_THEME);
      store.setIndicator(indicatorId);
      // …and, when the page is reporting for one unit, the map opens on that unit: the
      // figure the reader clicked was read *there*, and an explorer landing on the whole
      // country would be showing them a different number. `setScope` owns the rest — the
      // global country follows the scope, and the reporting level stays inside it.
      if (unit) store.setScope(scopeFromUnit(unit));
      navigate('/');
    },
    [navigate, unit],
  );
  /**
   * §1 "View on map" on a Baseline sheet — the explorer on that sheet's own theme.
   *
   * A sheet is a theme (`bl_agroforestry`), not an indicator, so this is the catalogue
   * preview's gesture rather than the KPI tile's: `selectTheme` writes the rail's pick and
   * applies the theme's default, then the route changes. `domFilter` is not a permalink key
   * (`lib/permalink.ts`), which is why this is a store write and not a hash.
   *
   * The page's scoped unit is deliberately **not** pushed onto the map here. A baseline
   * sheet has no estimate below country and watershed level, so landing the explorer on the
   * commune the dashboard happens to be filtered to would open it on a blank panel.
   */
  const baselineMap = useCallback(
    (groupId: string) => {
      selectTheme(`${BASELINE_PREFIX}${groupId}`, { config, layers, indicators }, t);
      navigate('/');
    },
    [config, indicators, layers, navigate, t],
  );

  /**
   * §3 — the survey round the Baseline section is reading, `null` until the reader picks.
   *
   * State of the *page* rather than of the section, because it is a fetch parameter: the
   * whole block arrives on the one `/dashboard` payload, so choosing a round is a refetch,
   * not a filter over what is already here. It is deliberately not a permalink key — the
   * survey's rounds are not a view a reader shares — and it is dropped whenever the scope
   * moves, so a round that only one country ran cannot survive onto another's page.
   */
  const [baselinePeriod, setBaselinePeriod] = useState<number | null>(null);
  useEffect(() => {
    setBaselinePeriod(null);
  }, [country, unitId]);

  const dash = useQuery({
    queryKey: queryKeys.dashboard(country, year, dashDomain, unitId, baselinePeriod),
    queryFn: fetchDashboard,
  });

  /* ---- the §E8 report job: submit, then poll, exactly as §F5.10's export does ---- */
  const [phase, setPhase] = useState<ReportPhase>('idle');
  const [jobId, setJobId] = useState<string | null>(null);
  const timer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const alive = useRef(true);
  useEffect(() => {
    // Set on the way *in*, not only cleared on the way out: React 18 StrictMode mounts,
    // unmounts and remounts a component in development, and a ref left `false` by that
    // first teardown would make every later `alive.current` check drop the report on the
    // floor — the submit would resolve into a component that thinks it is gone.
    alive.current = true;
    return () => {
      alive.current = false;
      if (timer.current) clearTimeout(timer.current);
    };
  }, []);

  /*
    A report is an artefact *of one scope*. Move the country, the year or the domain and the
    link on screen points at a PDF of what the reader was looking at before — so the job is
    dropped with the scope that produced it, and the button offers to build the new one.
  */
  useEffect(() => {
    if (timer.current) clearTimeout(timer.current);
    setPhase('idle');
    setJobId(null);
  }, [country, year, dashDomain, unitId]);

  const poll = useCallback(
    (id: string, delay: number, deadline: number) => {
      timer.current = setTimeout(() => {
        void fetchAnalysisJob(id)
          .then((job) => {
            if (!alive.current) return;
            if (job.status === 'done') {
              setPhase('done');
              toast(t('dashboard.reportReady'));
              return;
            }
            if (job.status === 'error') {
              setPhase('error');
              toast(job.error ?? t('dashboard.reportFailed'));
              return;
            }
            if (Date.now() > deadline) {
              setPhase('error');
              toast(t('dashboard.reportFailed'));
              return;
            }
            poll(id, nextDelay(delay), deadline);
          })
          .catch((err: unknown) => {
            if (!alive.current) return;
            setPhase('error');
            toast(err instanceof Error ? err.message : t('dashboard.reportFailed'));
          });
      }, delay);
    },
    [t],
  );

  const runReport = () => {
    setPhase('running');
    setJobId(null);
    void submitReport({ country, year, domain: dashDomain, unit: unitId })
      .then((submission) => {
        if (!alive.current) return;
        setJobId(submission.job_id);
        // A §E7 cache hit answers with the artefact already written — no poll, no wait.
        if (submission.status === 'done' || submission.cached) {
          setPhase('done');
          toast(t('dashboard.reportReady'));
          return;
        }
        if (submission.status === 'error') {
          setPhase('error');
          toast(submission.error ?? t('dashboard.reportFailed'));
          return;
        }
        toast(t('dashboard.reportQueued'));
        poll(submission.job_id, POLL_START_MS, Date.now() + POLL_TIMEOUT_MS);
      })
      .catch((err: unknown) => {
        if (!alive.current) return;
        setPhase('error');
        toast(err instanceof Error ? err.message : t('dashboard.reportFailed'));
      });
  };

  const payload = dash.data;
  /**
   * The geography every heading on the page names — the **scoped unit** when there is one,
   * the country otherwise (controller ruling, fix round 1).
   *
   * Taken from `payload.scope.unit`, like the scope line and for the same reason: the
   * subtitle and the targets card label *the figures below them*, so they must move when the
   * answer does, not when the select does. Until a scoped payload lands they still say the
   * country, which is what the numbers on screen are still for.
   */
  const scope =
    payload?.scope.unit?.name ?? t(`country.${country}`, COUNTRY_NAMES[country]);
  /**
   * §1 — the payload split into the client's numbered sections (client, 2026-09-16: *"section
   * it systematically: i. Contractual indicators, ii. Outcome indicators, iii. Baseline
   * information, and the rest can follow"*).
   *
   * `splitByTier` reads the server's own `tier`, falling back to the indicator code where a
   * cached pre-tier payload has none — so the page sections a stale answer rather than
   * printing an empty dashboard over it. Nothing is dropped: the three buckets between them
   * hold every KPI and every target row the one-rail page used to print.
   */
  const split = payload ? splitByTier(payload.kpis, payload.targets) : null;
  const hasContractual = Boolean(
    split && (split.contractual.kpis.length > 0 || split.contractual.targets.length > 0),
  );
  const hasOutcome = Boolean(
    split && (split.outcome.kpis.length > 0 || split.outcome.targets.length > 0),
  );
  const hasBaseline = Boolean(payload?.baseline && payload.baseline.sheets.length > 0);
  /*
    The domain filter narrows §C1's curated rail and the results-framework rows to the one
    domain picked (client, 2026-10-08: *"the results do not seem to change when I select
    different domain categories"*) — it does change them, but five of the eight domains have
    no row in either bucket, so the whole section used to vanish with nothing on screen to
    say why. A domain that empties a section this way still gets the section, with a sentence
    in place of its cards, rather than disappearing exactly as it would if the filter had
    silently done nothing — the one case this is for, and the only one these two flags fire
    on: `split` is non-null (the payload answered) and a domain is the reason it is empty.
  */
  const contractualEmptyForDomain = Boolean(split && !hasContractual && dashDomain);
  const outcomeEmptyForDomain = Boolean(split && !hasOutcome && dashDomain);
  /*
    The nav's own list, memoised on the *keys* rather than on the objects: `SectionNav`
    re-observes five elements whenever `items` changes identity, and a fresh array every
    render would tear the observer down and build it again on every keystroke in a filter.
  */
  const navKeys = [
    hasContractual || contractualEmptyForDomain ? 'contractual' : '',
    hasOutcome || outcomeEmptyForDomain ? 'outcome' : '',
    hasBaseline ? 'baseline' : '',
  ]
    .filter(Boolean)
    .join(',');
  const navItems = useMemo(
    () =>
      sectionSpec
        .filter((section) => navKeys.split(',').includes(section.key))
        .map((section) => ({ ...section, label: t(`dashboard.nav.${section.key}`) })),
    [navKeys, t],
  );

  const activeIdx = YEARS.indexOf(year);
  const prevYear = activeIdx > 0 ? (YEARS[activeIdx - 1] ?? null) : null;
  const domainColor = (id: string | null) =>
    config?.domains.find((d) => d.id === id)?.color ?? '#8B958A';

  return (
    <div className="view dash-view" data-testid="dashboard-page">
      <div className="dash-wrap">
        <div className="dash-head">
          {/*
            Title and subtitle on the left, the two actions on the same baseline to the
            right (client, 2026-09-16). The Soil Values logo is gone from the page: the
            topbar already carries it a hundred pixels above, and printing it twice was
            what pushed the heading off the page's own left edge.
          */}
          <div className="dash-title">
            <h2>{t('dashboard.title')}</h2>
            {/*
              The scope line is the subtitle now — "{scope} · reporting year {year}" says
              whose figures these are in one line rather than two stacked ones. The test id
              still appears only once the *scoped* payload has landed, because that is the
              moment the page can honestly name a unit (and §F7's own e2e reads it as the
              signal that it has).
            */}
            <p
              className="dash-sub"
              data-testid={payload?.scope.unit ? 'dash-scope-line' : undefined}
            >
              {/*
                `components` rather than a bare `<Trans>`: `b` is not in i18next's
                `transKeepBasicHtmlNodesFor` default list, so the tag in the string would
                be printed as literal text instead of emboldening the year.
              */}
              <Trans
                i18nKey="dashboard.subtitle"
                values={{ scope, year }}
                components={{ b: <b /> }}
              />
            </p>
          </div>
          <div className="dash-actions">
            <a
              className="btn ghost"
              data-testid="dash-csv"
              href={dashboardCsvUrl(country, dashDomain, unitId)}
              download={csvFilename(country, unitId)}
            >
              <Icon name="download" size={14} />
              {t('dashboard.exportCsv')}
            </a>
            <button
              type="button"
              className="btn primary"
              data-testid="dash-pdf"
              onClick={runReport}
              disabled={phase === 'running'}
            >
              <Icon name="download" size={14} />
              {phase === 'running' ? t('dashboard.reportQueued') : t('dashboard.reportPdf')}
            </button>
            {phase === 'done' && jobId && (
              <a
                className="btn ghost"
                data-testid="dash-pdf-link"
                href={`/api/v1/analysis/results/${jobId}.pdf`}
                target="_blank"
                rel="noreferrer"
              >
                <Icon name="download" size={14} />
                {t('dashboard.reportOpen')}
              </a>
            )}
          </div>
        </div>

        {/*
          One card, one fixed grid of seven labelled fields — geography, the four scope
          rungs, domain and year — every select the same width and height, every label
          above its own control. The old bar was a `flex-wrap` row of nine mixed-width
          items, which is what the client saw wrapping into three ragged lines.
        */}
        <div className="dash-filters">
          <div className="dash-field">
            <label htmlFor="dashCountry">{t('dashboard.filters.geography')}</label>
            <select
              id="dashCountry"
              data-testid="dash-country"
              value={country}
              onChange={(e) => {
                // Both in one handler: the country and the unit it invalidates move
                // together, so the page never fires a `?unit=` read for a unit it is about
                // to drop.
                setUnit(null);
                setCountry(e.target.value as Country);
              }}
            >
              {COUNTRIES.map((c) => (
                <option key={c} value={c}>
                  {t(`country.${c}`, COUNTRY_NAMES[c])}
                </option>
              ))}
            </select>
          </div>

          {/*
            The two branches of §C, in the vocabulary the map's cascade already uses
            (`hierarchy.filter.*`): one set of words for one hierarchy, so a reader who
            filtered the map to Yaadga meets the same four labels here.
          */}
          <ScopeSelect
            id="dashRegion"
            testId="dash-region"
            label={t('hierarchy.filter.region')}
            anyLabel={t('hierarchy.filter.allRegions')}
            value={pickedRegion}
            options={regions}
            onPick={setUnit}
          />
          <ScopeSelect
            id="dashCommune"
            testId="dash-commune"
            label={t('hierarchy.filter.commune')}
            anyLabel={t('hierarchy.filter.allCommunes')}
            value={pickedCommune}
            options={communes}
            disabled={!pickedRegion}
            onPick={(o) => setUnit(o ?? regionOption)}
          />
          <ScopeSelect
            id="dashWatershed"
            testId="dash-watershed"
            label={t('hierarchy.filter.watershed')}
            anyLabel={t('hierarchy.filter.allWatersheds')}
            value={pickedWatershed}
            options={watersheds}
            onPick={setUnit}
          />
          <ScopeSelect
            id="dashPartnership"
            testId="dash-partnership"
            label={t('hierarchy.filter.partnership')}
            anyLabel={t('hierarchy.filter.allPartnerships')}
            value={pickedPartnership}
            options={partnerships}
            onPick={setUnit}
          />

          <div className="dash-field">
            <label htmlFor="dashDomain">{t('dashboard.filters.domain')}</label>
            <select
              id="dashDomain"
              data-testid="dash-domain"
              value={dashDomain ?? ''}
              onChange={(e) => setDashDomain(e.target.value || null)}
            >
              <option value="">{t('dashboard.filters.allDomains')}</option>
              {(config?.domains ?? []).map((d) => (
                <option key={d.id} value={d.id}>
                  {d.name}
                </option>
              ))}
            </select>
          </div>

          <div className="dash-field">
            <label htmlFor="dashYear">{t('dashboard.filters.year')}</label>
            <select
              id="dashYear"
              data-testid="dash-year"
              value={year}
              onChange={(e) => setDashYear(Number(e.target.value))}
            >
              {YEARS.map((y) => (
                <option key={y} value={y}>
                  {y}
                </option>
              ))}
            </select>
          </div>
        </div>

        {payload?.restricted_hidden && !dashDomain && (
          <div className="note" data-testid="dash-restricted-note">
            <Icon name="lock" size={13} /> {t('dashboard.restrictedNote')}
          </div>
        )}

        {dash.isPending ? (
          <div className="note info" data-dashloading="">
            {t('dashboard.loading')}
          </div>
        ) : dash.isError || !payload ? (
          <div className="note" data-dasherror="">
            {t('dashboard.failed')}
          </div>
        ) : (
          <>
            {/*
              The sticky navigator. It sits under the filter card and above the first
              section, so the reader meets the page's structure before its first figure.
            */}
            <SectionNav items={navItems} />

            {/* ---- I. Contractual indicators ---- */}
            {split && (hasContractual || contractualEmptyForDomain) && (
              <DashSection
                sectionKey="contractual"
                numeral={NUMERAL.contractual}
                title={t('dashboard.sections.contractual.title')}
              >
                {!hasContractual ? (
                  <p className="dash-domain-empty" data-testid="dash-contractual-domain-empty">
                    {t('dashboard.filters.domainEmpty')}
                  </p>
                ) : (
                  <>
                <div className="kpis dash-heroes">
                  {split.contractual.kpis.map((kpi, i) => (
                    <HeroKpiTile
                      key={kpi.id}
                      kpi={kpi}
                      index={i}
                      color={domainColor(kpi.domain)}
                      activeIdx={activeIdx}
                      prevYear={prevYear}
                      /* The ring needs the target *row*: the tile's own `target` carries a
                         percentage but not the status or the source that say what kind of
                         commitment it is. */
                      target={split.contractual.targets.find((row) => row.id === kpi.id)}
                      onMap={canMap ? viewOnMap : undefined}
                    />
                  ))}
                </div>
                {/* The two charts side by side, the bars filtered to this section's rows.
                    Drawn whatever the year carries: a reporting year with no commitment at
                    all is a chart of four groups saying "no target set", which is the fact,
                    rather than a missing card the reader has to interpret. */}
                <div className="grid2">
                  <TargetsCard
                    payload={withTargets(payload, split.contractual.targets)}
                    year={year}
                    /* The four contractual indicators are the chart's groups, so an
                       indicator with no commitment this year is still on it, saying so. */
                    rail={split.contractual.kpis}
                    /* The group's colour is the indicator's own domain colour, looked up
                       through the rail: a target row carries no domain of its own. */
                    color={(id) =>
                      domainColor(payload.kpis.find((kpi) => kpi.id === id)?.domain ?? null)
                    }
                  />
                  <TrendCard payload={payload} />
                </div>
                {/*
                  The commentary on *this section*, folded away below 1180 px — narrowed to
                  the contractual rows like the two cards above it, or section I would carry
                  the sentences about section II's indicators as well.
                */}
                <InsightsCard payload={withTargets(payload, split.contractual.targets)} />
                  </>
                )}
              </DashSection>
            )}

            {/* ---- II. Outcome indicators ---- */}
            {split && (hasOutcome || outcomeEmptyForDomain) && (
              <DashSection
                sectionKey="outcome"
                numeral={NUMERAL.outcome}
                title={t('dashboard.sections.outcome.title')}
                description={t('dashboard.sections.outcome.desc')}
              >
                {!hasOutcome ? (
                  <p className="dash-domain-empty" data-testid="dash-outcome-domain-empty">
                    {t('dashboard.filters.domainEmpty')}
                  </p>
                ) : (
                <>
                {/*
                  §1 round 3 — one card per outcome indicator, built from `targets[]` rather
                  than from the rail: all seven results-framework indicators belong in this
                  section, including the five with no figure yet, and the rail carries only
                  the ones that have one. The row-by-row table under them is gone; each card
                  carries its own status, figure, commitment and "View on map".
                */}
                <OutcomeCards
                  cards={outcomeCards(split.outcome.kpis, split.outcome.targets)}
                  color={domainColor}
                  activeIdx={activeIdx}
                  onMap={canMap ? viewOnMap : undefined}
                />
                </>
                )}
              </DashSection>
            )}

            {/* ---- III. Baseline 2025 ---- */}
            {payload.baseline && hasBaseline && (
              <DashSection
                sectionKey="baseline"
                numeral={NUMERAL.baseline}
                title={t('dashboard.sections.baseline.title')}
              >
                {/*
                  The section header prints the title now, so the block's own heading is
                  suppressed — two titles, one above the other, saying the same thing was
                  the first thing the re-section had to lose. The fallback note stays: it
                  is the survey's own intro, not its title.
                */}
                <BaselineSection
                  baseline={payload.baseline}
                  unitLabel={payload.scope.unit?.name ?? null}
                  onMap={baselineMap}
                  onPeriod={setBaselinePeriod}
                  showTitle={false}
                />
              </DashSection>
            )}

          </>
        )}

        {/*
          Provenance is a footnote, not a filter: the source stamp used to ride at the end
          of the filter bar, where it competed with the controls for the same row. It reads
          under the cards, with the aggregation rule the subtitle no longer carries.
        */}
        <div className="dash-foot">
          <span>{t('dashboard.footNote')}</span>
          <span data-testid="dash-source">
            {payload
              ? payload.source.last_sync
                ? t('dashboard.source', {
                    label: payload.source.label,
                    ts: syncStamp(payload.source.last_sync),
                  })
                : t('dashboard.sourceNever', { label: payload.source.label })
              : ''}
          </span>
        </div>
      </div>
    </div>
  );
}

export default DashboardPage;
